import { StaffApiSchema } from "@apiUtils/index";
import { Button, Row, TextInput } from "@narmi/design_system";
import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import styles from "./SearchBar.module.css";
import { SearchPreview } from "./SearchPreview";

type SettingsSearchResult = StaffApiSchema.components["schemas"]["SettingsSearchResult"];

export function SearchBar({
  label = "Search",
  initialSearchValue = "",
  searchResults,
  totalResultCount,
  isSearchLoading,
  institutionCategoriesWithCount,
  searchCategory,
  onSelectSearchCategory,
  onDebouncedChange,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  label?: string;
  initialSearchValue?: string;
  searchResults: SettingsSearchResult[];
  totalResultCount?: number;
  isSearchLoading: boolean;
  institutionCategoriesWithCount: { name: string; setting_count: number }[];
  searchCategory?: string;
  onDebouncedChange: (debouncedValue: string) => void;
  onSelectSearchCategory: (category: string | undefined) => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}) {
  const [search, setSearch] = useState(initialSearchValue);
  // Following a result closes the preview without discarding the query, so the
  // user can reopen the same result list by focusing the input again.
  const [isOpen, setIsOpen] = useState(false);
  const timeout = useRef<NodeJS.Timeout | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!wrapperRef.current || !(target instanceof Node) || wrapperRef.current.contains(target))
        return;
      setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const onChange = useCallback(
    (value: string) => {
      clearTimeout(timeout.current!);
      timeout.current = setTimeout(() => {
        onDebouncedChange(value.trim());
      }, 500); // Debounce delay of 500ms
    },
    [onDebouncedChange]
  );
  // The name onDebouncedChange is misleading in this case, it is intended to let
  // consumers of this component know that the typical behavior is to debounce the change
  const makeUndebouncedChange = (value: string) => {
    clearTimeout(timeout.current!);
    setSearch(value);
    setIsOpen(true);
    onDebouncedChange(value.trim());
  };

  useEffect(() => {
    return () => {
      clearTimeout(timeout.current!);
    };
  }, []);

  useEffect(() => {
    setSearch(initialSearchValue);
  }, [initialSearchValue]);

  return (
    <div className={styles.searchBarWrapper} ref={wrapperRef}>
      <TextInput
        startIcon="search"
        value={search}
        label={label}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          // fire off search immediately when user presses Enter
          if (e.key === "Enter") {
            makeUndebouncedChange(search);
          }
        }}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setSearch(e.target.value);
          setIsOpen(true);
          onChange(e.target.value);
        }}
        onFocus={() => setIsOpen(true)}
        endContent={
          <Row gapSize="s" alignItems="center">
            {search && (
              <Row.Item>
                <Button label="Clear" kind="plain" onClick={() => makeUndebouncedChange("")} />
              </Row.Item>
            )}
          </Row>
        }
      />
      {search && isOpen && (
        <div className={styles.searchPreviewContainer}>
          <SearchPreview
            searchResults={searchResults}
            totalResultCount={totalResultCount}
            selectedCategory={searchCategory}
            onSelectSearchCategory={onSelectSearchCategory}
            onSelectSearchResult={() => setIsOpen(false)}
            isSearchLoading={isSearchLoading}
            institutionCategoriesWithCount={institutionCategoriesWithCount}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={onLoadMore}
          />
        </div>
      )}
    </div>
  );
}
